import {
  HiHandRaised,
  HiMiniCheckCircle,
  HiMiniExclamationCircle,
  HiMiniExclamationTriangle,
  HiMiniXCircle,
} from 'react-icons/hi2';
import { Center, Group, Text, Tooltip } from '@mantine/core';
import AppIcon from './AppIcon';
import AllRunsIcon from '@/assets/icons/all_runs.svg?react';
import ActiveStatusSpinner from '@/components/ActiveStatusSpinner.tsx';
import InProgressRunsIcon from '@/assets/icons/in_progress_runs.svg?react';
import { ExecutionStatus } from '@/types/statuses.ts';
import { StepExecutionTerminalReasonContext } from '@/types/stepExecutions.ts';
import { getTerminalReasonPresentationFromParts } from '@/utils/terminalReasonPresentation.ts';

type props = {
  status?: ExecutionStatus | null;
  terminalReasonCode?: string | null;
  terminalReasonContext?: StepExecutionTerminalReasonContext | null;
  currentStepPath?: string;
  isUpstreamSkipped?: boolean;
  include_text?: boolean;
  useSpinner?: boolean;
};

type ActiveIconProps = {
  useSpinner: boolean;
  color?: string;
};

const ActiveIcon = ({ useSpinner, color }: ActiveIconProps) => {
  return useSpinner ? (
    <ActiveStatusSpinner color={color} />
  ) : (
    <AppIcon CustomIcon={InProgressRunsIcon} size={'16px'} />
  );
};

const StatusIndicator = ({
  status,
  terminalReasonCode,
  terminalReasonContext,
  currentStepPath,
  isUpstreamSkipped = false,
  include_text = true,
  useSpinner = true,
}: props) => {
  const tooltipStyles = {
    tooltip: {
      fontSize: '12px',
      padding: '6px 10px',
    },
  };
  const isBlocked = getTerminalReasonPresentationFromParts({
    status,
    terminalReasonCode,
    terminalReasonContext,
    currentStepPath,
  }).isBlockedByExternalUpstream;

  return (
    <Group gap={'10px'} mr={'6px'} wrap={'nowrap'}>
      {(!status || status === ExecutionStatus.All) && (
        <>
          <Tooltip label="No runs yet" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
              }}
            >
              <AppIcon CustomIcon={AllRunsIcon} size={'16px'} iconProps={{ color: '#757575' }} />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              No status
            </Text>
          )}
        </>
      )}

      {status === ExecutionStatus.AskUser && (
        <>
          <Tooltip label="Needs input before continuing" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <Center
                w={'15px'}
                h={'15px'}
                style={{ borderRadius: '50%', backgroundColor: '#6F74F1' }}
              >
                <AppIcon Icon={HiHandRaised} size={'10px'} iconProps={{ color: '#F6F6F6' }} />
              </Center>
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Needs input
            </Text>
          )}
        </>
      )}

      {status === ExecutionStatus.Completed && (
        <>
          <Tooltip label="Successful" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <AppIcon Icon={HiMiniCheckCircle} size={'18px'} iconProps={{ color: '#0fa357' }} />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Succeeded
            </Text>
          )}
        </>
      )}
      {status === ExecutionStatus.PartiallyCompleted && !isUpstreamSkipped && (
        <>
          <Tooltip label="Partially Completed" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <AppIcon
                Icon={HiMiniExclamationTriangle}
                size={'16px'}
                iconProps={{ color: '#FF761F' }}
              />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Partially Completed
            </Text>
          )}
        </>
      )}
      {[ExecutionStatus.Errors, ExecutionStatus.PartiallyCompleted].includes(
        status as ExecutionStatus,
      ) &&
        isUpstreamSkipped && (
          <>
            <Tooltip
              label="Protected work did not start because upstream work ended first"
              position="top"
              styles={tooltipStyles}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '20px',
                  width: '20px',
                }}
              >
                <AppIcon Icon={HiMiniXCircle} size={'18px'} iconProps={{ color: '#94A2B3' }} />
              </span>
            </Tooltip>
            {include_text && (
              <Text fw={600} size="sm">
                Skipped
              </Text>
            )}
          </>
        )}
      {status === ExecutionStatus.Errors && !isBlocked && !isUpstreamSkipped && (
        <>
          <Tooltip label="Failed" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <AppIcon
                Icon={HiMiniExclamationCircle}
                size={'18px'}
                iconProps={{ color: '#EE393D' }}
              />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Error
            </Text>
          )}
        </>
      )}

      {status === ExecutionStatus.Errors && isBlocked && !isUpstreamSkipped && (
        <>
          <Tooltip label="Blocked by upstream step" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <AppIcon Icon={HiMiniXCircle} size={'18px'} iconProps={{ color: '#94A2B3' }} />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Blocked
            </Text>
          )}
        </>
      )}

      {status === ExecutionStatus.Cancelled && (
        <>
          <Tooltip label="Cancelled" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <AppIcon Icon={HiMiniXCircle} size={'18px'} iconProps={{ color: '#94A2B3' }} />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              Cancelled
            </Text>
          )}
        </>
      )}

      {/* Combined all "in progress" states into a single condition */}
      {status &&
        [
          ExecutionStatus.Preprocessing,
          ExecutionStatus.Pending,
          ExecutionStatus.Active,
          ExecutionStatus.RerunPreparing,
        ].includes(status) && (
          <>
            <Tooltip
              label={status === ExecutionStatus.RerunPreparing ? 'Preparing rerun' : 'In progress'}
              position="top"
              styles={tooltipStyles}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '20px',
                  width: '20px',
                }}
              >
                <ActiveIcon useSpinner={useSpinner} />
              </span>
            </Tooltip>
            {include_text && (
              <Text fw={600} size="sm">
                {status === ExecutionStatus.RerunPreparing ? 'Preparing rerun' : 'In progress'}
              </Text>
            )}
          </>
        )}
      {status === ExecutionStatus.ActiveWithErrors && (
        <>
          <Tooltip label="In progress (partially failed)" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <ActiveIcon useSpinner={useSpinner} color="#EE393D" />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              In progress (partially failed)
            </Text>
          )}
        </>
      )}
      {status === ExecutionStatus.Rerunning && (
        <>
          <Tooltip label="In progress (rerunning)" position="top" styles={tooltipStyles}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '20px',
                width: '20px',
              }}
            >
              <ActiveIcon useSpinner={useSpinner} />
            </span>
          </Tooltip>
          {include_text && (
            <Text fw={600} size="sm">
              In progress (rerunning)
            </Text>
          )}
        </>
      )}
    </Group>
  );
};

export default StatusIndicator;
